import { useAuthStore } from '../store/auth';
import { useAcademyContextStore } from '../store/academyContext';
import { useAcademy } from '../queries/academy';
import {
  formatDateForRegion,
  formatDateTimeForRegion,
  type Region,
} from '@court-sync/shared';

export interface UseFormatDateOptions {
  /** When set, skips loading the owner academy context. */
  region?: Region | null;
  /** Public academy/booking surfaces — never loads `/academies/user`. */
  publicContext?: boolean;
}

/**
 * Returns date formatters using an academy region (US vs AU).
 * On public pages pass `publicContext: true` and `region` from the loaded academy.
 */
export function useFormatDate(options?: UseFormatDateOptions) {
  const authRegion = useAuthStore((s) => s.region);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated && s.isInitialized);
  const selectedAcademyId = useAcademyContextStore((s) => s.selectedAcademyId);
  const needsAcademyRegion =
    !options?.publicContext &&
    options?.region == null &&
    isAuthenticated &&
    !!selectedAcademyId;
  const { academy } = useAcademy(selectedAcademyId, { enabled: needsAcademyRegion });
  const region =
    options?.region ?? academy?.region ?? (options?.publicContext ? 'US' : authRegion) ?? 'US';

  const formatDate = (
    date: Date | string,
    regionOverride?: Region | null,
    formatOptions?: Intl.DateTimeFormatOptions
  ) => formatDateForRegion(date, regionOverride ?? region ?? 'US', formatOptions);

  const formatDateTime = (
    date: Date | string,
    regionOverride?: Region | null,
    formatOptions?: Intl.DateTimeFormatOptions
  ) => formatDateTimeForRegion(date, regionOverride ?? region ?? 'US', formatOptions);

  return { formatDate, formatDateTime, region };
}
